import { AxiosRequestConfig } from "axios";
import { Spinner } from "components";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  GetLiveRateForL2,
  GetLiveRateForL2ReqType,
} from "service/apis/Opportunities";
import { toast } from "sonner";

const PURPOSES = [
  { id: "20", label: "Education" },
  { id: "3", label: "Study Maintance" },
  { id: "4", label: "Gift/Maintance" },
];

const isMarketOpen = () => {
  const now = new Date();
  const day = now.getDay();
  if (day === 0 || day === 6) return false;

  const currentTime = now.getHours() * 60 + now.getMinutes();
  const marketStart = 10 * 60 ; // 10:00 AM in minutes
  const marketEnd = 15 * 60 ; // 3:00 PM in minutes

  return currentTime >= marketStart && currentTime <= marketEnd;
};

const isMarketClosed = () => !isMarketOpen();

export const LiveRateSlider = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPurposeId, setSelectedPurposeId] = useState(PURPOSES[0].id);
  const [rates, setRates] = useState<any[]>([]);

  const formatRate = (rawRate: any): string => {
    if (!rawRate || typeof rawRate !== "string") return "--";
    const match: RegExpMatchArray | null = rawRate?.match(/^\d+(\.\d+)?/);
    if (match === null) return "--";
    const num = parseFloat(match[0]);
    return isNaN(num) ? "--" : num.toFixed(2);
  };

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(el);
    checkScroll();

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      resizeObserver.disconnect();
    };
  }, []);

  const handleScroll = (direction: "left" | "right") => {
    scrollRef.current?.scrollBy({
      left: direction === "right" ? 160 * 7 : -160 * 7,
      behavior: "smooth",
    });
  };

  const fetchLiveRateList = async (purpose_id: string, showLoader = true) => {
    // if (isMarketClosed()) {
    //   console.debug("Market closed: skipping live rate fetch");
    //   return;
    // }

    if (showLoader) setLoading(true);

    try {
      const authToken = JSON.parse(localStorage.getItem("authToken") || "");
      const payload: AxiosRequestConfig<GetLiveRateForL2ReqType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        data: { purpose_id: Number(purpose_id) },
      };
      const res = await GetLiveRateForL2(payload);
      if (res.status === 200) {
        setRates(Array.isArray(res.data) ? res.data : []);
        // Set last updated time and if it 3 pm then set 3 pm
        const now = new Date();
        if (now.getHours() >= 15 ) {
          setLastUpdated(new Date(now.setHours(15, 0, 0, 0)));
        } else {
          setLastUpdated(new Date());
        }
      } else {
        setRates([]);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Live rate API error");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // 1. Call API ONCE when page loads (even if market is closed)
  useEffect(() => {
    fetchLiveRateList(selectedPurposeId, true);
  }, []);

  // 2. Call API when dropdown changes, but ONLY if market is open
  useEffect(() => {
    if (!isMarketOpen()) return;
    fetchLiveRateList(selectedPurposeId, true);
  }, [selectedPurposeId]);

  // 3. Polling every 10 seconds ONLY if market is open
  useEffect(() => {
    if (!isMarketOpen()) return;
    const interval = setInterval(() => {
      fetchLiveRateList(selectedPurposeId, false);
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedPurposeId]);

  return (
    <>
      {/* {loading && <Spinner />} */}
      <div className="w-full h-auto bg-white-A700 rounded-xl shadow-sm px-4 py-3 flex flex-col gap-4 relative">
        {/* Header */}
        <div className="flex items-center justify-between text-xs text-gray-700">
          <div>
            <p className="text-[#165FE3] font-semibold text-lg">
              Live Rates{" "}
              <span className="text-black ml-1">
                {lastUpdated
                  ? lastUpdated.toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })
                  : "--"}
              </span>
            </p>
            <p className="text-sm text-[#666666] font-normal mt-0.5">
              Last updated{" "}
              <span className="ml-1">
                {lastUpdated
                  ? lastUpdated.toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })
                  : "--"}
              </span>
            </p>
          </div>
        </div>

        {/* Live IBR & Dropdown */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-[#666666]">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            Live Rate
          </div>
          <div className="relative inline-block w-fit">
            <select
              value={selectedPurposeId}
              onChange={(e) => setSelectedPurposeId(e.target.value)}
              disabled={isMarketClosed()}
              className={`appearance-none bg-[#F8F8F8] border-none text-sm font-medium text-[#666666] 
                rounded-[8px] px-3 py-[10px] pr-9 leading-tight focus:outline-none
                ${isMarketClosed() ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {PURPOSES.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={18}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-[#666666] pointer-events-none"
            />
          </div>
        </div>

        {/* Market closed message */}
        <p className="text-[#333333] text-[14px] text-center font-medium mt-1">
          Live Rates is available only during market hours from Monday to
          Friday, 10:00 AM – 3:00 PM IST
        </p>

        {/* Slider */}
        {
          !isMarketClosed() && 
          <div className="relative">
            <div
              ref={scrollRef}
              className="flex items-center gap-3 overflow-x-auto scrollbar-hide pr-4 hide-scrollbar"
            >
              {rates?.length > 0 ? (
                rates.map((rate, index) => (
                  <div
                    key={index}
                    className={`w-[153px] h-[67px] flex-shrink-0 flex items-center justify-between p-3 
                      rounded-[16px] border border-[#F9F9F9] shadow-[0px_0px_12px_0px_#0000000F]
                      ${
                        isMarketClosed()
                          ? "bg-gray-100 opacity-50 pointer-events-none"
                          : "bg-white"
                      }`}
                  >
                    <div className="flex flex-col items-start justify-center gap-1">
                      <span className="text-[12px] font-semibold text-[#333333] bg-[#F2F2F2] px-1.5 py-0.5 rounded-md leading-tight">
                        {rate?.currency ?? "--"}
                      </span>
                      <span className="text-[16px] font-semibold text-[#165FE3] leading-none">
                        {formatRate(rate?.rate)}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400">No rates available</p>
              )}
            </div>

            {/* Arrows */}
            {canScrollLeft && (
              <button
                onClick={() => handleScroll("left")}
                className="absolute p-[8px] left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-md w-9 h-9 flex items-center justify-center border border-[#165FE3] bg-[#fff]"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            {canScrollRight && (
              <button
                onClick={() => handleScroll("right")}
                className="absolute p-[8px] right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-md w-9 h-9 flex items-center justify-center border border-[#165FE3] bg-[#fff]"
              >
                <ChevronRight size={20} />
              </button>
            )}
          </div>
        }
      </div>
    </>
  );
};
